/**
 * iter245 — MFPL Bank Master modal + receiving-account picker.
 *
 * Two React components:
 *   - `<MFPLBankMasterModal>` — SA-only CRUD list (add/edit/deactivate,
 *     mark default) — opened from the onboarding page.
 *   - `<MFPLBankPicker>`      — reusable dropdown that shows the
 *     currently-resolved MFPL account (masked) + a Change link that
 *     opens a picker modal.  Used by the Commercial Setup card + the
 *     Invoice Create wizard.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Ban, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const mask = (a) => {
  if (!a) return '';
  const s = String(a);
  return '••••' + s.slice(-4);
};

async function bankCall(path, body, method = 'POST') {
  const res = await fetch(`${API}/api/mfpl-banks${path}`, {
    method, headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const j = await res.json();
  if (!res.ok) throw new Error(j.detail || 'Request failed');
  return j;
}

const EMPTY_FORM = {
  display_name: '', bank_name: '',
  account_holder: 'MANASWINI FOODS PRIVATE LIMITED',
  account_number: '', ifsc: '', branch: '',
  account_type: '', upi_id: '', swift: '',
  currency: 'INR', country: 'India',
  payment_instructions: '', is_default: false,
};

export function MFPLBankMasterModal({ open, onClose, token, isSA }) {
  const [rows, setRows] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // {id?, ...form}

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const r = await bankCall('/list', { token, include_inactive: showInactive });
      setRows(r.items || []);
    } catch (e) { toast.error(e.message); }
  }, [open, showInactive, token]);
  useEffect(() => { load(); }, [load]);

  const saveForm = async () => {
    const f = editing;
    if (!f.display_name || !f.bank_name || !f.account_number || !f.ifsc) {
      toast.error('Display Name, Bank, Account No. and IFSC are required'); return;
    }
    try {
      if (f.id) {
        await bankCall(`/update/${f.id}`, { token, ...f, reason: 'edit via UI' });
        toast.success('Bank updated');
      } else {
        await bankCall('/create', { token, ...f });
        toast.success('Bank created');
      }
      setEditing(null); load();
    } catch (e) { toast.error(e.message); }
  };

  const setDefault = async (id) => {
    try { await bankCall(`/set-default/${id}`, { token }); toast.success('Default set'); load(); }
    catch (e) { toast.error(e.message); }
  };

  const deactivate = async (id, name) => {
    const reason = window.prompt(`Deactivate ${name}? Reason:`);
    if (!reason) return;
    try { await bankCall(`/deactivate/${id}`, { token, reason }); toast.success('Deactivated'); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="mfpl-bank-master-modal">
        <DialogHeader>
          <DialogTitle>MFPL Bank Accounts — Master</DialogTitle>
        </DialogHeader>

        <div className="flex justify-between items-center py-2">
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Include inactive
          </label>
          {isSA && <Button size="sm" onClick={() => setEditing({ ...EMPTY_FORM })} data-testid="btn-mfpl-bank-add"><Plus className="w-4 h-4 mr-1" />Add Bank Account</Button>}
        </div>

        {rows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-6 text-center">No bank accounts yet.</div>
        ) : (
          <table className="w-full text-xs" data-testid="mfpl-bank-table">
            <thead className="border-b text-left"><tr><th className="p-1">Display</th><th>Bank / Branch</th><th>A/c (masked)</th><th>IFSC</th><th>Currency</th><th>Status</th><th className="w-40 text-right">Actions</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className={`border-b ${!r.active ? 'bg-muted/30' : ''}`} data-testid={`mfpl-bank-row-${r.id}`}>
                  <td className="p-1">
                    <div className="font-medium">{r.display_name}</div>
                    <div className="text-[10px] text-muted-foreground">{r.account_holder}</div>
                  </td>
                  <td>{r.bank_name}<div className="text-[10px] text-muted-foreground">{r.branch}</div></td>
                  <td className="font-mono">{r.account_number_masked || mask(r.account_number)}</td>
                  <td className="font-mono">{r.ifsc}</td>
                  <td>{r.currency}</td>
                  <td className="space-x-1">
                    {r.is_default && <Badge className="bg-amber-500">DEFAULT</Badge>}
                    {r.active ? <Badge variant="outline" className="text-green-700 border-green-500">ACTIVE</Badge> : <Badge variant="outline" className="text-red-700 border-red-500">INACTIVE</Badge>}
                  </td>
                  <td className="text-right space-x-1">
                    {isSA && r.active && !r.is_default && <Button size="sm" variant="ghost" onClick={() => setDefault(r.id)} data-testid={`btn-bank-default-${r.id}`}><Star className="w-3 h-3" /></Button>}
                    {isSA && <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })} data-testid={`btn-bank-edit-${r.id}`}><Edit2 className="w-3 h-3" /></Button>}
                    {isSA && r.active && !r.is_default && <Button size="sm" variant="ghost" onClick={() => deactivate(r.id, r.display_name)} data-testid={`btn-bank-deact-${r.id}`}><Ban className="w-3 h-3 text-red-500" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Add/Edit form */}
        <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-2xl" data-testid="mfpl-bank-form-modal">
            <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'Add'} MFPL Bank Account</DialogTitle></DialogHeader>
            {editing && (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><Label>Display Name *</Label><Input value={editing.display_name} onChange={e => setEditing(f => ({ ...f, display_name: e.target.value }))} data-testid="bank-form-display" /></div>
                <div><Label>Bank Name *</Label><Input value={editing.bank_name} onChange={e => setEditing(f => ({ ...f, bank_name: e.target.value }))} data-testid="bank-form-bank" /></div>
                <div className="col-span-2"><Label>Account Holder *</Label><Input value={editing.account_holder} onChange={e => setEditing(f => ({ ...f, account_holder: e.target.value }))} data-testid="bank-form-holder" /></div>
                <div><Label>Account Number *</Label><Input value={editing.account_number} onChange={e => setEditing(f => ({ ...f, account_number: e.target.value }))} data-testid="bank-form-acct" /></div>
                <div><Label>IFSC *</Label><Input value={editing.ifsc} onChange={e => setEditing(f => ({ ...f, ifsc: e.target.value.toUpperCase() }))} data-testid="bank-form-ifsc" /></div>
                <div className="col-span-2"><Label>Branch *</Label><Input value={editing.branch} onChange={e => setEditing(f => ({ ...f, branch: e.target.value }))} data-testid="bank-form-branch" /></div>
                <div><Label>Account Type</Label><Input value={editing.account_type} placeholder="Current / Savings" onChange={e => setEditing(f => ({ ...f, account_type: e.target.value }))} /></div>
                <div><Label>UPI ID</Label><Input value={editing.upi_id} onChange={e => setEditing(f => ({ ...f, upi_id: e.target.value }))} /></div>
                <div><Label>SWIFT</Label><Input value={editing.swift} onChange={e => setEditing(f => ({ ...f, swift: e.target.value.toUpperCase() }))} /></div>
                <div><Label>Currency</Label><Input value={editing.currency} onChange={e => setEditing(f => ({ ...f, currency: e.target.value.toUpperCase() }))} /></div>
                <div><Label>Country</Label><Input value={editing.country} onChange={e => setEditing(f => ({ ...f, country: e.target.value }))} /></div>
                <div className="col-span-2"><Label>Payment Instructions</Label><Textarea rows={2} value={editing.payment_instructions} onChange={e => setEditing(f => ({ ...f, payment_instructions: e.target.value }))} /></div>
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" checked={editing.is_default} onChange={e => setEditing(f => ({ ...f, is_default: e.target.checked }))} data-testid="bank-form-default" />
                  <span className="text-xs">Mark as MFPL default account</span>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveForm} data-testid="btn-bank-form-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}


/** Small read-only card for the Onboarding Dashboard tab. */
export function MFPLBankAssignedCard({ call, token, oid, isSA, onOpenMaster }) {
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await call(`/resolve/${oid}`, {}, '/api/mfpl-banks');
      setBank(r.bank);
    } catch (e) { console.warn(e.message); }
    finally { setLoading(false); }
  }, [call, oid]);
  useEffect(() => { load(); }, [load]);

  const openAssign = async () => {
    try {
      const r = await bankCall('/list', { token });
      setRows(r.items); setSelectedId(bank?.id || ''); setShowAssign(true);
    } catch (e) { toast.error(e.message); }
  };
  const doAssign = async () => {
    if (!selectedId) { toast.error('Pick a bank'); return; }
    try {
      await bankCall('/assign-franchise', { token, onboarding_id: oid, bank_account_id: selectedId, reason: 'Onboarding UI' });
      toast.success('Franchise bank assigned');
      setShowAssign(false); load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="border rounded p-3 bg-slate-50" data-testid="mfpl-bank-card">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-primary">MFPL RECEIVING ACCOUNT</div>
        <div className="flex gap-1">
          {isSA && <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={onOpenMaster} data-testid="btn-bank-master">Master</Button>}
          {isSA && <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={openAssign} data-testid="btn-bank-change">Change</Button>}
        </div>
      </div>
      {loading ? <div className="text-xs text-muted-foreground">Loading…</div> :
        bank ? (
          <div className="text-xs pt-1 space-y-0.5">
            <div className="font-semibold">{bank.bank_name} · {bank.display_name}</div>
            <div className="font-mono">A/c: {mask(bank.account_number)}</div>
            <div className="text-muted-foreground">{bank.branch}</div>
          </div>
        ) : (
          <div className="text-xs text-amber-700 pt-1">No MFPL bank account configured. Click Master to add one.</div>
        )
      }

      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-xl" data-testid="assign-bank-modal">
          <DialogHeader><DialogTitle>Assign MFPL Bank to this Franchise</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {rows.map(r => (
              <label key={r.id} className={`flex items-center gap-2 border rounded p-2 cursor-pointer ${selectedId === r.id ? 'bg-primary/5 border-primary' : ''}`}>
                <input type="radio" name="bank" checked={selectedId === r.id} onChange={() => setSelectedId(r.id)} data-testid={`assign-radio-${r.id}`} />
                <div className="flex-1 text-xs">
                  <div className="font-semibold">{r.display_name} {r.is_default && <Badge className="ml-1 bg-amber-500 text-[9px]">DEFAULT</Badge>}</div>
                  <div>{r.bank_name} · <span className="font-mono">{r.account_number_masked || mask(r.account_number)}</span> · {r.ifsc} · {r.branch}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={doAssign} data-testid="btn-assign-bank-save">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
